import { useCallback } from "react";

import type { StaffRole } from "../lib/auth-contract";
import {
  roleHasCapability,
  type CentralClientStaffCapability,
} from "../lib/central-role-policy";
import { useStaffAuth } from "../providers/StaffAuthProvider";

export type CentralCapabilityContext = {
  /** The authenticated staff role, or `null` while signed out / loading. */
  role: StaffRole | null;
  /**
   * Capability check against the client mirror of the backend role policy
   * (`src/lib/central-role-policy.ts`). Unknown or signed-out states deny.
   */
  can: (capability: CentralClientStaffCapability) => boolean;
};

/**
 * Central UI gating hook. Prefer `can("capability")` over comparing
 * `identity.access.role` by hand so the client stays aligned with
 * `functions/src/staff-role-policy.ts`.
 */
export function useCentralCapability(): CentralCapabilityContext {
  const { identity } = useStaffAuth();
  const role = identity?.access.role ?? null;
  const can = useCallback(
    (capability: CentralClientStaffCapability) =>
      roleHasCapability(role, capability),
    [role],
  );
  return { can, role };
}
